// import components
import Button from '@sdb/ui/atoms/Button';
import { Box, FormControlLabel, Switch } from '@mui/material';
import {
  StyledBoxFlexJustifyEnd,
  StyledBoxFlexJustifyEndGap
} from 'components/shared/Box/Box.styles';
import MenuButton from 'components/shared/MenuButton';
import { DateNavigation } from 'components/shared';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import SwitcherComponent from 'components/admin-portal/BaseTabsRouting/components/Headers/SwitcherComponent';
import {
  DateNavigationContainer,
  PlanUnitSelectorContainer,
  StyledHeader,
  StyledUnsavedDataAlertBox,
  Top
} from './Header.styles';

// import external libraries
import { useTranslation } from 'react-i18next';

// import types
import { ClaimsAction } from 'components/roster-portal/home/travel-expense-claims/types';
import { HeaderProps } from './Header.types';
import UnsavedDataAlert from 'components/shared/CRUD/components/InnerPage/components/UnsavedDataAlert';
import { flattenToDate } from 'utils/date';

const Header = (props: HeaderProps) => {
  const {
    onAction,
    onSelectedDate,
    onSelectPlanUnit,
    selectedDate,
    selection = [],
    showActions,
    showPlanUnitSelector,
    isTravelMovements = false,
    onlyApprovableTravelMovements = true,
    handleToggleChange,
    showSwitcherComponent = false,
    switcherComponentData,
    showSingleAction = false,
    onSingleAction,
    singleActionButtonLabel,
    showCustomMenuItemLabel,
    menuOptions,
    unsavedDataAlert,
    unsavedDataAlertContent,
    allowDateNavigationMenuOptions = false,
    dateNavigationOptions
  } = props;
  const { t } = useTranslation('common');

  const defaultOptions = [
    {
      label: t(`travelExpenseClaims.actions.${ClaimsAction.APPROVE}`),
      onClick: () => onAction?.(ClaimsAction.APPROVE)
    },
    {
      label: t(`travelExpenseClaims.actions.${ClaimsAction.REJECT}`),
      onClick: () => onAction?.(ClaimsAction.REJECT)
    }
  ];

  return (
    <StyledHeader as="header">
      <Top>
        <Box>
          {showPlanUnitSelector && onSelectPlanUnit && (
            <PlanUnitSelectorContainer>
              <PlanUnitSelector onSelected={onSelectPlanUnit} />
              {isTravelMovements && (
                <FormControlLabel
                  control={<Switch checked={onlyApprovableTravelMovements} />}
                  label={`${t('travelMovements.toBeApproved')}`}
                  onChange={handleToggleChange}
                />
              )}
            </PlanUnitSelectorContainer>
          )}

          {showSwitcherComponent && switcherComponentData && (
            <SwitcherComponent
              tabbedElement={switcherComponentData?.tabbedElement}
              tabulatedItems={switcherComponentData?.tabulatedItems}
              setTabbedElement={switcherComponentData?.setTabbedElement}
              disableRouting={switcherComponentData?.disableRouting}
              showCustomMenuItemLabel={showCustomMenuItemLabel}
            />
          )}
        </Box>
        <DateNavigationContainer>
          <DateNavigation
            defaultPeriod="month"
            defaultDate={selectedDate}
            onDateChange={(date) => onSelectedDate(flattenToDate(date))}
            options={dateNavigationOptions}
            allowMenuOptions={allowDateNavigationMenuOptions}
          />
        </DateNavigationContainer>
        <StyledBoxFlexJustifyEndGap>
          {unsavedDataAlert && (
            <StyledUnsavedDataAlertBox>
              <UnsavedDataAlert textContent={unsavedDataAlertContent} />
            </StyledUnsavedDataAlertBox>
          )}

          {showSingleAction && onSingleAction && singleActionButtonLabel && (
            <StyledBoxFlexJustifyEnd>
              <Button variant="contained" onClick={onSingleAction}>
                {singleActionButtonLabel}
              </Button>
            </StyledBoxFlexJustifyEnd>
          )}

          {showActions && (
            <StyledBoxFlexJustifyEnd>
              <MenuButton
                label={t('actions')}
                disabled={selection.length === 0}
                options={menuOptions ?? defaultOptions}
              />
            </StyledBoxFlexJustifyEnd>
          )}
        </StyledBoxFlexJustifyEndGap>
      </Top>
    </StyledHeader>
  );
};

export default Header;
